import { isMeasurement, transformToCSS } from './utilities.helper'

const themeConstants = {
  border: {
    full: 'border',
    bottom: 'border-bottom',
    top: 'border-top'
  }
} as const

const remCalculator = (pxValue: number | string, basePX = 16): string =>
  `${(1 / basePX) * (typeof pxValue === 'string' ? parseInt(pxValue, 10) : pxValue)}rem`

const hexWithTransparency = (colour: string, transparency = 1): string => {
  let hexValue: string = Math.round(255 * transparency)
    .toString(16)
    .toUpperCase()
  if (hexValue.length < 2) {
    hexValue = `0${hexValue}`
  }
  return colour + hexValue
}

const createObjValue = (item: string[]): string => {
  const objValue: any = item[1]
  if (typeof objValue === 'string') {
    return isMeasurement(item) ? remCalculator(objValue) : objValue.toString()
  }
  return objValue
}

const createCSSObject = (data: Record<string, unknown>): string => {
  const convertedArray: any[] = Object.entries(data)
  return convertedArray.reduce((combinedString: string, item: string[]) => {
    const objKey: string = transformToCSS(item[0])
    const objValue: string = createObjValue(item)
    const createCSSItem = `${objKey}: ${objValue}; `
    return combinedString + createCSSItem
  }, '')
}

const createBorder = (width: number | string, color: string, type: string = themeConstants.border.full): string =>
  createCSSObject({ [type]: `${width} solid ${color}` })

const colorTokens = (theme?: any): Record<string, number | string> => {
  if (!theme) {
    return null
  }

  const { monochrome, palette, content, semantic }: any = theme.color

  // Old colours left, to be deleted later on
  switch (theme.channel) {
    case 'tu':
      return {
        ...content,
        darker: monochrome.darker,
        'red-60': palette['red-60'],
        'red-40': palette['red-40'],
        'blue-60': palette['blue-60'],
        'blue-40': palette['blue-40'],
        'pink-60': palette['pink-60'],
        'pink-40': palette['pink-40'],
        'dark-blue-60': palette['dark-blue-60'],
        'dark-blue-40': palette['dark-blue-40'],
        'strong-blue-60': palette['strong-blue-60'],
        'strong-blue-40': palette['strong-blue-40'],
        'soft-red-60': palette['soft-red-60'],
        'soft-red-40': palette['soft-red-40'],
        'faux-malachite-60': palette['faux-malachite-60'],
        'faux-malachite-40': palette['faux-malachite-40'],
        'selective-yellow-60': palette['selective-yellow-60'],
        'selective-yellow-40': palette['selective-yellow-40']
      }
    case 'habitat':
      return {
        ...content,
        darker: monochrome.darker,
        'grey-charcoal': content['grey-darker'],
        'red-sale': content.sale,
        'blue-new-in': content.new,
        'promotional-red': palette['red-90'], // #8D0000
        'promotional-red-light': palette['red-30'], // #EC8A8A
        'promotional-blue': palette['blue-90'], // #114E8B
        'promotional-blue-light': palette['blue-30'] // #95C0EA
      }
    case 'sainsburys':
      return {
        ...content,
        darker: monochrome.darker,
        'brand-1': palette['brand-1'],
        'info-lighter': semantic.info.lighter
      }
    case 'nectar':
      return {
        ...content,
        darker: monochrome.darker,
        purple: palette.purple,
        'purple-darkest': palette['purple-90']
      }
    default:
      return {
        ...content,
        darker: monochrome.darker,
        'grey-gunmetal': '#4f5a65',
        'grey-charcoal': monochrome.darker,
        red: palette?.red,
        rust: palette?.red70,
        mauve: '#781F74',
        'mauve-light': '#781F74',
        'mauve-mid': '#781F74',
        'dark-navy': palette['blue-100'],
        'blue-cobalt': palette['blue-100'],
        cerulean: '#69c',
        'cerulean-light': '#69c',
        'green-kelly': palette['green-60'],
        'raw-sienna': '#95664c'
      }
  }
}

const colorToLunaToken = (textColorValue: string, theme: object): number | string | null => {
  const availableColours: Record<string, number | string> = colorTokens(theme)

  return availableColours[textColorValue] || null
}

const colorSelector = (textColor: string, defaultColor: string, theme: object): number | string =>
  colorToLunaToken(textColor, theme) || defaultColor

export {
  themeConstants,
  remCalculator,
  hexWithTransparency,
  createCSSObject,
  createBorder,
  colorTokens,
  colorToLunaToken,
  colorSelector
}
